import React, { useState, useEffect, useRef } from 'react';
import { User } from 'lucide-react';

// Normalize passenger names from airline formats for display.
// "LASTNAME/FIRSTNAME MR" -> "Firstname Lastname", "JOHN SMITH" -> "John Smith".
export const formatPassengerName = (rawName) => {
  if (!rawName) return null;

  // Capitalize helper: "JOHN" -> "John", "john" -> "John"
  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Check if it's in airline format: LASTNAME/FIRSTNAME or LASTNAME/FIRSTNAMEMR
  if (rawName.includes('/')) {
    const parts = rawName.split('/');
    const lastName = parts[0] || '';
    let firstName = parts[1] || '';

    // Remove common title suffixes (MR, MRS, MS, MISS, DR, etc.)
    firstName = firstName.replace(/(MR|MRS|MS|MISS|DR|PROF|SIR|JR|SR|II|III|IV)$/i, '').trim();

    // Handle middle names/initials in firstName (e.g., "JAMES W" -> "James W")
    const firstNameParts = firstName.split(/\s+/).map(part => {
      // Keep single letters as uppercase (initials)
      if (part.length === 1) return part.toUpperCase();
      return capitalize(part);
    });

    return `${firstNameParts.join(' ')} ${capitalize(lastName)}`.trim();
  }

  // Not in airline format - check if it's ALL CAPS and needs normalizing
  if (rawName === rawName.toUpperCase() && rawName.length > 2) {
    // Split by spaces and capitalize each part
    return rawName.split(/\s+/).map(part => {
      if (part.length === 1) return part.toUpperCase();
      return capitalize(part);
    }).join(' ');
  }

  // Already in proper format
  return rawName;
};

// Reusable component for displaying passenger/guest names with smart truncation.
// Shows as many full names as fit in the container on a single line, with a
// "+N" hover tooltip for the rest. Entries may be plain strings or
// { name, emphasis } objects — emphasized names render bold (e.g. executives).
// Callers may still pass `variant` ('simple' | 'modern'); it no longer changes
// the look, since both themes share the ui-* tokens.
export const PassengerNameList = ({ names, size = 'xs', IconComponent = User }) => {
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(1);

  // Normalize entries to { name, emphasis }
  const entries = (names || [])
    .map(n => (typeof n === 'string' ? { name: n, emphasis: false } : n))
    .filter(e => e && e.name);

  // Character widths vary by text size
  const isSm = size === 'sm';
  const CHAR_WIDTH = isSm ? 7.5 : 6.5; // text-sm is ~7.5px, text-xs is ~6.5px
  const ICON_WIDTH = isSm ? 22 : 20; // Icon + gap
  const PLUS_BADGE_WIDTH = isSm ? 40 : 35; // "+N" badge width
  const NAME_GAP = isSm ? 10 : 8; // Comma + space between names

  const namesKey = entries.map(e => e.name).join('|');

  useEffect(() => {
    const calculateVisibleNames = () => {
      if (!containerRef.current || entries.length === 0) return;

      const containerWidth = containerRef.current.offsetWidth;
      if (containerWidth === 0) return;

      // Available width after icon
      let availableWidth = containerWidth - ICON_WIDTH;

      // If we have more than 1 name, we need to reserve space for the "+N" badge
      const needsBadge = entries.length > 1;
      if (needsBadge) {
        availableWidth -= PLUS_BADGE_WIDTH;
      }

      let count = 0;
      let usedWidth = 0;

      for (let i = 0; i < entries.length; i++) {
        const nameWidth = entries[i].name.length * CHAR_WIDTH;
        const separatorWidth = i > 0 ? NAME_GAP : 0;
        const totalNeeded = nameWidth + separatorWidth;

        if (usedWidth + totalNeeded <= availableWidth) {
          usedWidth += totalNeeded;
          count++;
        } else {
          break;
        }
      }

      // Always show at least 1 name
      setVisibleCount(Math.max(1, count));
    };

    calculateVisibleNames();

    // Recalculate on resize
    const resizeObserver = new ResizeObserver(calculateVisibleNames);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [namesKey, CHAR_WIDTH, ICON_WIDTH, PLUS_BADGE_WIDTH, NAME_GAP]); // eslint-disable-line

  if (entries.length === 0) return null;

  const visibleEntries = entries.slice(0, visibleCount);
  const remainingEntries = entries.slice(visibleCount);
  const remainingCount = remainingEntries.length;

  // Style variants based on size
  const textSizeClass = isSm ? 'text-sm' : 'text-xs';
  const textClass = `${textSizeClass} text-ui-ink-2`;
  const iconClass = 'w-3.5 h-3.5 text-ui-muted flex-shrink-0';
  const badgeClass = 'text-xs text-ui-muted flex-shrink-0 px-1.5 py-0.5 bg-ui-hover rounded hover:bg-ui-line transition-colors';
  const tooltipClass = 'absolute bottom-full left-0 mb-2 px-3 py-2 bg-ui-raised text-ui-ink text-xs rounded-lg shadow-ui-pop border border-ui-line opacity-0 invisible group-hover/passengertooltip:opacity-100 group-hover/passengertooltip:visible transition-all duration-200 z-50';

  return (
    <div ref={containerRef} className={`flex items-center gap-1 ${textClass} relative`}>
      <IconComponent className={iconClass} />
      <span className="truncate">
        {visibleEntries.map((entry, idx) => (
          <span key={idx}>
            {idx > 0 && ', '}
            <span className={entry.emphasis ? 'font-semibold' : undefined}>{entry.name}</span>
          </span>
        ))}
      </span>
      {remainingCount > 0 && (
        <span className="group/passengertooltip relative cursor-pointer">
          <span className={badgeClass}>+{remainingCount}</span>
          <span className={tooltipClass}>
            {remainingEntries.map((entry, idx) => (
              <div key={idx} className="whitespace-nowrap">{entry.name}</div>
            ))}
            <span className="absolute top-full left-4 border-4 border-transparent border-t-ui-raised"></span>
          </span>
        </span>
      )}
    </div>
  );
};

export default PassengerNameList;
